import { useTranslation } from "react-i18next";
import { Outlet, useOutletContext, useSearchParams } from "react-router";
import { SubNav } from "../components/SubNav";
import { useRecruitmentOpenings } from "../lib/api";
import Candidate from "./recruitment/Candidate";
import Channels from "./recruitment/Channels";
import Correspondence from "./recruitment/Correspondence";
import CreateJob from "./recruitment/CreateJob";
import Opening from "./recruitment/Opening";
import Paperwork from "./recruitment/Paperwork";
import RequestPosition from "./recruitment/RequestPosition";
import OpeningsList from "./recruitment/OpeningsList";
import { usePeopleCompany } from "./People";

/** Recruitment (WP-23.1, OS-0616): openings, one opening, one candidate — each at its own address.
 *
 * This was a single page that stacked the create-and-post form, the openings table, the shortlist and the whole
 * candidate card on top of each other, revealing the last two only once you had clicked. A shortlisted candidate
 * could not be linked to, so an interviewer could not be sent to the person they were about to meet. */

type RecruitmentContext = { company: string; isHr: boolean };

export default function Recruitment() {
  const { t } = useTranslation();
  const company = usePeopleCompany();
  const [params] = useSearchParams();
  const query = params.get("company") ? `?company=${encodeURIComponent(params.get("company")!)}` : "";
  // the openings call is shared with the list screen through the query cache, so this costs no extra request
  const openings = useRecruitmentOpenings(company || undefined);
  const isHr = openings.data?.is_hr ?? false;

  const items = [
    { key: "openings", to: `/people/recruitment${query}`, label: t("rec.tabs.openings"), end: true },
    { key: "channels", to: `/people/recruitment/channels${query}`, label: t("rec.tabs.channels") },
    { key: "paperwork", to: `/people/recruitment/paperwork${query}`, label: t("rec.tabs.paperwork") },
    ...(isHr ? [{ key: "correspondence", to: `/people/recruitment/correspondence${query}`, label: t("rec.tabs.correspondence") }] : []),
    ...(isHr ? [{ key: "new", to: `/people/recruitment/new${query}`, label: t("rec.tabs.new") }] : []),
  ];

  return (
    <div data-recruitment>
      <SubNav items={items} label={t("rec.sections")} />
      <Outlet context={{ company, isHr } satisfies RecruitmentContext} />
    </div>
  );
}

const useRec = () => useOutletContext<RecruitmentContext>();

export const RecruitmentIndex = () => <OpeningsList company={useRec().company || undefined} />;
export const RecruitmentNew = () => <CreateJob />;
export const RecruitmentRequest = () => <RequestPosition />;
export const RecruitmentChannels = () => <Channels company={useRec().company || undefined} />;
export const RecruitmentPaperwork = () => <Paperwork company={useRec().company || undefined} />;
export const RecruitmentCorrespondence = () => <Correspondence />;
export const RecruitmentOpening = () => <Opening isHr={useRec().isHr} />;
export const RecruitmentCandidate = () => <Candidate />;
